import { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import { ListItem } from '@tiptap/extension-list';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo2,
  Undo2,
} from 'lucide-react';

import type { WebsiteBlock } from '@/api/schema';
import { Button } from './Button';
import { Field } from './Field';
import { FilePicker } from './FilePicker';
import { Input } from './Input';

import { articleBlocks, articleDocument, articleLink } from './richTextDocument';
import styles from './RichTextEditor.module.css';

const extensions = [
  StarterKit.configure({
    heading: { levels: [2] },
    link: { openOnClick: false, autolink: false, isAllowedUri: (url) => !!articleLink(url) },
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    listItem: false,
    strike: false,
    underline: false,
  }),
  ListItem.extend({ content: 'paragraph' }),
  Image.extend({
    addAttributes() {
      return { ...this.parent?.(), imageId: { default: null, rendered: false } };
    },
    parseHTML: () => [],
  }),
];

export function RichTextEditor({
  label,
  initial,
  busy,
  required = false,
  error,
  imageUrl,
  selectImage,
  onChange,
  onBusy,
}: {
  label: string;
  initial: WebsiteBlock[];
  busy: boolean;
  required?: boolean;
  error?: string | undefined;
  imageUrl: (id: string) => string;
  selectImage: (file: File) => Promise<string>;
  onChange: (body: WebsiteBlock[]) => void;
  onBusy?: (change: number) => void;
}) {
  const [tool, setTool] = useState<'link' | 'image' | null>(null);
  const [url, setUrl] = useState('');
  const [image, setImage] = useState<{ id: string; name: string } | null>(null);
  const [alt, setAlt] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [imageError, setImageError] = useState('');
  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: articleDocument(initial, imageUrl),
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': label,
        'aria-multiline': 'true',
        'aria-required': String(required),
      },
    },
    onUpdate: ({ editor }) => {
      onChange(articleBlocks(editor.getJSON()));
    },
  });
  useEffect(() => {
    editor?.setEditable(!busy, false);
  }, [editor, busy]);
  if (!editor) return null;

  async function pickImage(file: File) {
    setPreparing(true);
    setImageError('');
    onBusy?.(1);
    try {
      const id = await selectImage(file);
      setImage({ id, name: file.name });
    } catch (error) {
      setImageError(error instanceof Error ? error.message : 'The image could not be added.');
    } finally {
      onBusy?.(-1);
      setPreparing(false);
    }
  }

  const tools = [
    {
      label: 'Section heading',
      icon: Heading2,
      active: editor.isActive('heading'),
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: 'Bold',
      icon: Bold,
      active: editor.isActive('bold'),
      action: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: 'Italic',
      icon: Italic,
      active: editor.isActive('italic'),
      action: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: 'Bulleted list',
      icon: List,
      active: editor.isActive('bulletList'),
      action: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numbered list',
      icon: ListOrdered,
      active: editor.isActive('orderedList'),
      action: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Link',
      icon: Link2,
      active: tool === 'link' || editor.isActive('link'),
      action: () => {
        setUrl(String(editor.getAttributes('link').href ?? ''));
        setTool((current) => (current === 'link' ? null : 'link'));
      },
    },
    {
      label: 'Image',
      icon: ImagePlus,
      active: tool === 'image',
      action: () => {
        setTool((current) => (current === 'image' ? null : 'image'));
      },
    },
    {
      label: 'Undo',
      icon: Undo2,
      disabled: !editor.can().undo(),
      action: () => editor.chain().focus().undo().run(),
    },
    {
      label: 'Redo',
      icon: Redo2,
      disabled: !editor.can().redo(),
      action: () => editor.chain().focus().redo().run(),
    },
  ];
  const href = articleLink(url);

  return (
    <section className={styles.articleEditor} aria-label={`Edit ${label.toLowerCase()}`}>
      <div className={styles.editorHeader}>
        <h3>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
        </h3>
        <p className={styles.help}>Write here and use the toolbar to format your content.</p>
      </div>
      <div className={styles.editorToolbar} role="group" aria-label="Article formatting">
        {tools.map(({ label, icon: Icon, active, disabled, action }) => (
          <Button
            key={label}
            size="sm"
            variant={active ? 'primary' : 'ghost'}
            aria-label={label}
            aria-pressed={active}
            title={label}
            disabled={busy || disabled}
            icon={<Icon size={16} aria-hidden="true" />}
            onClick={action}
          />
        ))}
      </div>
      {tool === 'link' ? (
        <div className={styles.editorTool}>
          <Field label="Link URL">
            {({ id }) => (
              <Input
                id={id}
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
              />
            )}
          </Field>
          <div className={styles.actions}>
            <Button
              size="sm"
              disabled={!href || busy}
              onClick={() => {
                if (!href) return;
                const chain = editor.chain().focus().extendMarkRange('link');
                if (editor.state.selection.empty && !editor.isActive('link')) {
                  chain
                    .insertContent({
                      type: 'text',
                      text: href,
                      marks: [{ type: 'link', attrs: { href } }],
                    })
                    .run();
                } else {
                  chain.setLink({ href }).run();
                }
                setTool(null);
              }}
            >
              Apply link
            </Button>
            {editor.isActive('link') ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  setTool(null);
                }}
              >
                Remove link
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      {tool === 'image' ? (
        <div className={styles.editorTool}>
          <Field
            label="Choose article image"
            hint="PNG, JPEG or WebP up to 5 MB."
            error={imageError || undefined}
          >
            {({ id, describedBy, invalid }) => (
              <FilePicker
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                accept="image/png,image/jpeg,image/webp"
                busy={preparing}
                fileName={image?.name ?? ''}
                onPick={(file) => void pickImage(file)}
              />
            )}
          </Field>
          {image ? (
            <Field label="Image description">
              {({ id }) => (
                <Input
                  id={id}
                  value={alt}
                  maxLength={250}
                  onChange={(event) => {
                    setAlt(event.target.value);
                  }}
                />
              )}
            </Field>
          ) : null}
          <Button
            size="sm"
            disabled={busy || preparing || !image || !alt.trim()}
            onClick={() => {
              if (!image) return;
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'image',
                  attrs: { src: imageUrl(image.id), imageId: image.id, alt: alt.trim() },
                })
                .run();
              setImage(null);
              setAlt('');
              setTool(null);
            }}
          >
            Insert image
          </Button>
        </div>
      ) : null}
      <EditorContent className={styles.editorBody} editor={editor} />
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
